import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError, pdfUrl } from "@/lib/api";
import { formatIDR, formatDate, BOOKING_STATUS } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FileText, Receipt, Warehouse, Undo2 } from "lucide-react";

export default function BookingDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [b, setB] = React.useState(null);

  const load = React.useCallback(async () => {
    const { data } = await api.get(`/bookings/${id}`);
    setB(data);
  }, [id]);

  React.useEffect(() => { load(); }, [load]);

  const setStatus = async (status) => {
    try { await api.post(`/bookings/${id}/status`, { status }); toast.success("Status diubah"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const createQuotation = async () => {
    try {
      const { data } = await api.post("/quotations", { booking_id: id });
      toast.success("Quotation dibuat: " + data.number);
      nav(`/quotations/${data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const createInvoice = async () => {
    try {
      const { data } = await api.post("/invoices", { booking_id: id });
      toast.success("Invoice dibuat: " + data.number);
      nav(`/invoices/${data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!b) return <div className="text-sm text-neutral-500">Memuat…</div>;
  const t = b.totals || {};

  return (
    <div className="space-y-4" data-testid="booking-detail">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-neutral-500">Booking</div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>
            {b.number} <StatusBadge statusMap={BOOKING_STATUS} value={b.status} className="ml-2" />
          </h1>
          <p className="text-sm text-neutral-500 mt-1">{b.project_name || "-"} · {formatDate(b.start_date)} – {formatDate(b.end_date)}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={createQuotation} data-testid="btn-create-quotation"><FileText className="h-4 w-4 mr-1" /> Buat Quotation</Button>
          <Button variant="outline" size="sm" onClick={createInvoice} data-testid="btn-create-invoice"><Receipt className="h-4 w-4 mr-1" /> Buat Invoice</Button>
          <Button variant="outline" size="sm" onClick={() => nav(`/checkout/${id}`)} data-testid="btn-goto-checkout"><Warehouse className="h-4 w-4 mr-1" /> Check-Out</Button>
          <Button variant="outline" size="sm" onClick={() => nav(`/return/${id}`)} data-testid="btn-goto-return"><Undo2 className="h-4 w-4 mr-1" /> Pengembalian</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card-flat p-4 lg:col-span-2 space-y-3">
          <div className="text-sm font-semibold">Equipment</div>
          <div className="overflow-x-auto">
            <table className="w-full data-table">
              <thead>
                <tr>
                  <th className="text-left">Nama</th>
                  <th className="text-right">Qty</th>
                  <th className="text-right">Hari</th>
                  <th className="text-right">Harga</th>
                  <th className="text-right">Subtotal</th>
                  <th className="text-left">Aset Assigned</th>
                </tr>
              </thead>
              <tbody>
                {b.items?.map((it, i) => (
                  <tr key={i}>
                    <td>{it.product_name || "-"}</td>
                    <td className="text-right">{it.qty}</td>
                    <td className="text-right">{it.days}</td>
                    <td className="text-right">{formatIDR(it.unit_price)}</td>
                    <td className="text-right font-medium">{formatIDR(it.qty * it.days * it.unit_price - (it.discount || 0))}</td>
                    <td className="text-xs">{(it.asset_ids || []).length} unit</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="border-t border-neutral-200 pt-3">
            <div className="text-sm font-semibold mb-2">Ubah Status</div>
            <div className="flex flex-wrap gap-2">
              {["draft", "confirmed", "preparing", "ready", "checked_out", "returned", "completed", "cancelled"].map((s) => (
                <Button key={s} variant={b.status === s ? "default" : "outline"} size="sm"
                  className={b.status === s ? "bg-green-600 hover:bg-green-700" : ""}
                  onClick={() => setStatus(s)} data-testid={`status-btn-${s}`}>
                  {BOOKING_STATUS[s]?.label || s}
                </Button>
              ))}
            </div>
          </div>
        </div>

        <div className="card-flat p-4 space-y-2 text-sm">
          <div className="text-sm font-semibold">Pelanggan</div>
          <div>{b.customer?.name}</div>
          <div className="text-neutral-500">{b.customer?.phone} · {b.customer?.email}</div>
          <div className="text-neutral-500 text-xs">{b.customer?.billing_address}</div>

          <div className="border-t border-neutral-200 pt-3 space-y-1">
            <div className="flex justify-between"><span className="text-neutral-500">Subtotal</span><span>{formatIDR(t.subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-neutral-500">Diskon</span><span>-{formatIDR((t.item_discount || 0) + (t.booking_discount || 0))}</span></div>
            <div className="flex justify-between"><span className="text-neutral-500">Biaya Kirim</span><span>{formatIDR(t.delivery_fee)}</span></div>
            <div className="flex justify-between"><span className="text-neutral-500">PPN</span><span>{formatIDR(t.tax)}</span></div>
            <div className="flex justify-between"><span className="text-neutral-500">Deposit</span><span>{formatIDR(t.deposit)}</span></div>
            <div className="flex justify-between font-bold text-base border-t border-neutral-200 pt-2">
              <span>Total</span><span className="text-green-700">{formatIDR(t.total)}</span>
            </div>
          </div>

          {(b.quotations?.length > 0 || b.invoices?.length > 0) && (
            <div className="border-t border-neutral-200 pt-3 space-y-1">
              {b.quotations?.map((q) => (
                <Link key={q.id} to={`/quotations/${q.id}`} className="block text-xs text-blue-700 hover:underline">
                  Quotation: {q.number}
                </Link>
              ))}
              {b.invoices?.map((iv) => (
                <Link key={iv.id} to={`/invoices/${iv.id}`} className="block text-xs text-blue-700 hover:underline">
                  Invoice: {iv.number}
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
